import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Slider } from 'primereact/slider';
import { SelectButton } from 'primereact/selectbutton';

import WallItemPreview from './WallItemPreview';
import { useResolveAt } from '../../../../queries/wallPreview';
import { useGetPlaylist } from '../../../../queries/wallPlaylistLibrary';
import { useGetWallMediaLibrary } from '../../../../queries/videoWall';
import { useGetThemes } from '../../../../queries/signageTemplates';

/**
 * Smart Template Engine — Preview System (Phase 2B): Schedule Preview.
 *
 * Scrub a weekday + hour and see which playlist would play then (resolved by the
 * REAL backend rules via /resolve-at) plus a preview of its first item. Read-only.
 */
const DAYS = [
  { label: 'Mon', value: 1 }, { label: 'Tue', value: 2 }, { label: 'Wed', value: 3 },
  { label: 'Thu', value: 4 }, { label: 'Fri', value: 5 }, { label: 'Sat', value: 6 }, { label: 'Sun', value: 7 },
];

function todayIso() {
  const d = new Date().getDay(); // 0=Sun..6=Sat
  return d === 0 ? 7 : d;
}

export default function ScheduleScrubber({ wallId, grid = { rows: 2, cols: 2 }, maxWidth = 560 }) {
  const { t } = useTranslation();
  const [dow, setDow] = useState(todayIso());
  const [hour, setHour] = useState(new Date().getHours());

  const { data: resolved } = useResolveAt(wallId, dow, hour * 60);
  const { data: playlist } = useGetPlaylist(resolved?.id, !!resolved?.id);

  const { data: mediaRaw } = useGetWallMediaLibrary(true);
  const { data: themeData } = useGetThemes();
  const themes = themeData?.themes ?? [];
  const defaultZones = useMemo(() => themeData?.defaultZones ?? {}, [themeData]);
  const mediaMap = useMemo(() => {
    const arr = mediaRaw?.data ?? mediaRaw ?? [];
    const m = {}; (Array.isArray(arr) ? arr : []).forEach((x) => { m[x.id] = x; }); return m;
  }, [mediaRaw]);
  const resolvePhoto = useMemo(() => (id) => mediaMap[id]?.path || mediaMap[id]?.thumb || null, [mediaMap]);

  const firstItem = playlist?.items?.[0] || null;
  const hh = String(hour).padStart(2, '0');

  return (
    <div className="d-flex flex-column align-items-center gap-3">
      <SelectButton value={dow} onChange={(e) => e.value && setDow(e.value)}
        options={DAYS.map((d) => ({ label: t(`video_wall.library.assign.day_${['', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'][d.value]}`), value: d.value }))} />

      <div style={{ width: 'min(100%, 460px)' }}>
        <div className="d-flex justify-content-between small mb-1" style={{ color: 'var(--pp-text-secondary)' }}>
          <span>{t('video_wall.sched_prev.time')}</span>
          <strong style={{ color: 'var(--pp-text-primary)' }}>{hh}:00</strong>
        </div>
        <Slider value={hour} onChange={(e) => setHour(e.value)} min={0} max={23} />
      </div>

      <div className="text-center">
        {resolved ? (
          <span className="badge" style={{ background: 'var(--pp-bg-tertiary)', color: 'var(--pp-text-primary)', fontSize: 13 }}>
            <i className="pi pi-play me-1" /> {resolved.name}
          </span>
        ) : (
          <span className="small text-muted">{t('video_wall.sched_prev.nothing')}</span>
        )}
      </div>

      {firstItem && (
        <WallItemPreview item={firstItem} grid={{ ...grid, orientation: 'landscape' }}
          mediaMap={mediaMap} themes={themes} defaultZones={defaultZones} resolvePhoto={resolvePhoto} maxWidth={maxWidth} />
      )}
    </div>
  );
}
